import { OnboardingStepName } from '../types/onboarding';

export const storeItem = <T>(key: string, value: T): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Swallow write failures so they don't escape as unhandled exceptions:
    //   - QuotaExceededError: Safari private mode / iOS WKWebView with
    //     storage disabled, or the quota genuinely exhausted. This app is
    //     frequently embedded in an iOS in-app browser (see WEB-A7/WEB-AP),
    //     where setItem can throw. LocalStorage is a best-effort cache, so a
    //     failed write should degrade silently rather than crash the caller
    //     (these setters run from click handlers / effects via
    //     useLocalStorageValue, so an uncaught throw surfaces to Sentry).
    //   - TypeError: JSON.stringify hitting a circular value (app source,
    //     distinct from the extension-driven WEB-AS noise which we filter in
    //     src/lib/sentry.ts).
    // `retrieveItem` below already swallows its symmetric parse failure; this
    // matches that contract.
    console.warn(`Failed to write localStorage key "${key}":`, error);
  }
};

export const retrieveItem = <T>(key: string): T | null => {
  const item = localStorage.getItem(key);
  if (!item) {
    return null;
  }
  try {
    return JSON.parse(item);
  } catch (error) {
    // If there's invalid JSON in localStorage, remove it and return null
    console.warn(`Invalid JSON found in localStorage for key "${key}":`, error);
    localStorage.removeItem(key);
    return null;
  }
};

export const clearItem = (key: string): void => {
  localStorage.removeItem(key);
};

const userIdKey = (userId: string, key: string) => `${userId}:${key}`;
const userProviderKey = (userId: string, providerId: string, key: string) =>
  `${userId}:${providerId}:${key}`;

export const storeUserItem = <T>(
  userId: string,
  key: string,
  value: T,
): void => {
  storeItem(userIdKey(userId, key), value);
};

export const retrieveUserItem = <T>(userId: string, key: string): T | null => {
  return retrieveItem(userIdKey(userId, key));
};

const clearUserItem = (userId: string, key: string): void => {
  clearItem(userIdKey(userId, key));
};

// Provider-scoped variants (preferred for multi-tenant context)
export const storeUserProviderItem = <T>(
  userId: string,
  providerId: string,
  key: string,
  value: T,
): void => {
  storeItem(userProviderKey(userId, providerId, key), value);
};

export const retrieveUserProviderItem = <T>(
  userId: string,
  providerId: string,
  key: string,
): T | null => {
  return retrieveItem(userProviderKey(userId, providerId, key));
};

export const clearUserProviderItem = (
  userId: string,
  providerId: string,
  key: string,
): void => {
  clearItem(userProviderKey(userId, providerId, key));
};

export const markOnboardingStepCompleted = (
  userEmail: string,
  onboardingStep: OnboardingStepName,
): void => {
  storeUserItem(userEmail, onboardingStep, true);
};

export const getOnboardingStepCompleted = (
  userEmail: string,
  onboardingStep: OnboardingStepName,
): boolean => {
  return retrieveUserItem<boolean>(userEmail, onboardingStep) ?? false;
};

export const clearOnboardingStepCompleted = (
  userId: string,
  onboardingStep: OnboardingStepName,
): void => {
  clearUserItem(userId, onboardingStep);
};

export const HAS_DISMISSED_CALENDAR_SETUP = 'hasDismissedCalendarSetup';
export const HAS_DISMISSED_SCHEDULE_SETUP = 'hasDismissedScheduleSetup';
export const HAS_DISMISSED_PAYMENTS_SETUP = 'hasDismissedPaymentsSetup';
export const HAS_DISMISSED_ADD_CALENDLY_CLIENTS_SETUP =
  'hasDismissedCalendlyClientsSetup';
export const HAS_DISMISSED_STOREFRONT_SETUP = 'hasDismissedStorefrontSetup';
export const HAS_DISMISSED_SERVICE_SETUP = 'hasDismissedServiceSetup';
export const HAS_DISMISSED_APPEARANCE_SETUP = 'hasDismissedAppearanceSetup';

export const HAS_DISMISSED_CHAT_NUDGE = 'hasDismissedChatNudge';

export const PATIENT_MANAGEMENT_COLUMN_ORDER = 'patientManagementColumnOrder';
export const PATIENT_MANAGEMENT_COLUMN_VISIBILITY =
  'patientManagementColumnVisibility';
export const PATIENT_MANAGEMENT_ACTIONS_VISIBILITY =
  'patientManagementActionsVisibility';
export const PATIENT_MANAGEMENT_PROVIDER_USER_FILTER =
  'patientManagementProviderUserFilter';
export const PATIENT_MANAGEMENT_TAG_FILTER_IDS =
  'patientManagementTagFilterIds';
export const PATIENT_MANAGEMENT_EXCLUDED_TAG_FILTER_IDS =
  'patientManagementExcludedTagFilterIds';
export const PATIENT_MANAGEMENT_EXCLUDED_PROVIDER_USER_FILTER_IDS =
  'patientManagementExcludedProviderUserFilterIds';
export const PATIENT_MANAGEMENT_TAGS_MATCH = 'patientManagementTagsMatch';
export const PATIENT_MANAGEMENT_PROVIDERS_MATCH =
  'patientManagementProvidersMatch';
export const PATIENT_MANAGEMENT_FILTER_OPERATOR =
  'patientManagementFilterOperator';
export const PATIENT_MANAGEMENT_NO_ASSIGNED_PRACTITIONERS =
  'patientManagementNoAssignedPractitioners';
export const PATIENT_MANAGEMENT_NO_TAGS = 'patientManagementNoTags';

export const CALENDAR__SELECTED_CALENDAR_IDS = 'calendar__selectedCalendarIds';
export const CALENDAR__SELECTED_RESOURCE_IDS = 'calendar__selectedResourceIds';

/** Insights practitioner card expand/collapse map: { [practitionerId]: boolean } */
export const INSIGHTS_PRACTITIONER_SECTIONS_EXPANDED =
  'insights.practitionerSectionsExpanded';

export const PATIENT_LIBRARY_FILTERS = 'patientLibraryFilters';
